import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Clock, Filter, Plus, Search, AlertCircle, Activity } from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import MetricCard from './components/MetricCard';
import IncidentTable from './components/IncidentTable';
import IncidentTrendChart from './components/IncidentTrendChart';
import ClassificationChart from './components/ClassificationChart';
import LocationChart from './components/LocationChart';
import InvestigationStatusChart from './components/InvestigationStatusChart';
import PendingInvestigationsPanel from './components/PendingInvestigationsPanel';
import { useSidebar } from '../../hooks/useSidebar';

const IncidentReportingDashboardContent = () => {
  const navigate = useNavigate();
  const { isCollapsed } = useSidebar();
  
  // Mock data for incidents
  const mockIncidents = [
    {
      id: 'INC-2024-001',
      classification: 'Near Miss',
      severity: 'Medium',
      location: 'Manufacturing Plant A',
      dateTime: '2024-12-03 14:30',
      investigator: 'John Smith',
      status: 'Under Investigation',
      priority: 'medium',
      description: 'Equipment malfunction narrowly avoided',
      daysOpen: 2
    },
    {
      id: 'INC-2024-002',
      classification: 'Injury',
      severity: 'High',
      location: 'Warehouse B',
      dateTime: '2024-12-02 09:15',
      investigator: 'Sarah Johnson',
      status: 'Awaiting Review',
      priority: 'high',
      description: 'Minor laceration during material handling',
      daysOpen: 3
    },
    {
      id: 'INC-2024-003',
      classification: 'Near Miss',
      severity: 'Low',
      location: 'Office Building',
      dateTime: '2024-12-01 16:45',
      investigator: 'Mike Wilson',
      status: 'Completed',
      priority: 'low',
      description: 'Wet floor without signage',
      daysOpen: 0
    },
    {
      id: 'INC-2024-004',
      classification: 'Injury',
      severity: 'Critical',
      location: 'Construction Site C',
      dateTime: '2024-11-30 11:20',
      investigator: 'Emily Davis',
      status: 'Under Investigation',
      priority: 'critical',
      description: 'Fall from elevated platform',
      daysOpen: 5
    },
    {
      id: 'INC-2024-005',
      classification: 'Near Miss',
      severity: 'Medium',
      location: 'Laboratory',
      dateTime: '2024-11-29 13:00',
      investigator: 'David Brown',
      status: 'Recommendations Pending',
      priority: 'medium',
      description: 'Chemical spill contained before exposure',
      daysOpen: 6
    }
  ];

  const [searchTerm, setSearchTerm] = useState('');
  const [classificationFilter, setClassificationFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState('30days');

  // Calculate metrics
  const metrics = useMemo(() => {
    const total = mockIncidents?.length;
    const nearMiss = mockIncidents?.filter(i => i?.classification === 'Near Miss')?.length;
    const injuries = mockIncidents?.filter(i => i?.classification === 'Injury')?.length;
    const fatalities = mockIncidents?.filter(i => i?.classification === 'Fatality')?.length;
    const underInvestigation = mockIncidents?.filter(i => i?.status === 'Under Investigation')?.length;
    const completed = mockIncidents?.filter(i => i?.status === 'Completed')?.length;
    const overdue = mockIncidents?.filter(i => i?.daysOpen > 7)?.length;

    return {
      total,
      nearMiss,
      injuries,
      fatalities,
      underInvestigation,
      completed,
      overdue,
      avgInvestigationTime: 4.2
    };
  }, [mockIncidents]);

  // Filter incidents
  const filteredIncidents = useMemo(() => {
    return mockIncidents?.filter(incident => {
      const matchesSearch = incident?.id?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                          incident?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase());
      const matchesClassification = classificationFilter === 'all' || incident?.classification === classificationFilter;
      const matchesLocation = locationFilter === 'all' || incident?.location === locationFilter;
      const matchesStatus = statusFilter === 'all' || incident?.status === statusFilter;
      
      return matchesSearch && matchesClassification && matchesLocation && matchesStatus;
    });
  }, [mockIncidents, searchTerm, classificationFilter, locationFilter, statusFilter]);

  const handleCreateIncident = () => {
    navigate('/incident-creation-form');
  };

  const handleExport = () => {
    console.log('Exporting incident data...');
  };

  return (
    <div className={`bg-background min-h-screen`}>
      <div className="p-6 lg:p-8">
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Incident Reporting Dashboard</h1>
              <p className="text-muted-foreground mt-1">
                Monitor, track, and analyze all safety incidents
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                iconName="Home"
                onClick={() => navigate('/')}
              >
                Return to Dashboard
              </Button>
              <Button
                variant="default"
                iconName="Plus"
                onClick={() => navigate('/incident-creation-form')}
              >
                Report Incident
              </Button>
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <MetricCard
            title="Total Incidents"
            value={metrics?.total}
            trend="+8%"
            trendUp={true}
            icon={<AlertTriangle className="w-5 h-5 text-red-600" />}
            color="red"
          />
          <MetricCard
            title="Near Miss"
            value={metrics?.nearMiss}
            trend="+12%"
            trendUp={true}
            icon={<AlertCircle className="w-5 h-5 text-yellow-600" />}
            color="yellow"
          />
          <MetricCard
            title="Injuries"
            value={metrics?.injuries}
            trend="-5%"
            trendUp={false}
            icon={<Activity className="w-5 h-5 text-orange-600" />}
            color="orange"
          />
          <MetricCard
            title="Under Investigation"
            value={metrics?.underInvestigation}
            trend={`${metrics?.overdue} overdue`}
            trendUp={metrics?.overdue > 0}
            icon={<Clock className="w-5 h-5 text-blue-600" />}
            color="blue"
          />
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <IncidentTrendChart />
          <ClassificationChart 
            nearMiss={metrics?.nearMiss}
            injuries={metrics?.injuries}
            fatalities={metrics?.fatalities}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <LocationChart />
          <InvestigationStatusChart 
            underInvestigation={metrics?.underInvestigation}
            completed={metrics?.completed}
            overdue={metrics?.overdue}
          />
        </div>

        {/* Pending Investigations Alert */}
        {metrics?.overdue > 0 && (
          <PendingInvestigationsPanel 
            overdueCount={metrics?.overdue}
            incidents={mockIncidents?.filter(i => i?.daysOpen > 7)}
          />
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-gray-600" />
            <h3 className="font-semibold text-gray-900">Filters</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search incidents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="pl-10"
              />
            </div>
            
            <Select
              value={classificationFilter}
              onChange={(e) => setClassificationFilter(e?.target?.value)}
            >
              <option value="all">All Classifications</option>
              <option value="Near Miss">Near Miss</option>
              <option value="Injury">Injury</option>
              <option value="Fatality">Fatality</option>
            </Select>

            <Select
              value={locationFilter}
              onChange={(e) => setLocationFilter(e?.target?.value)}
            >
              <option value="all">All Locations</option>
              <option value="Manufacturing Plant A">Manufacturing Plant A</option>
              <option value="Warehouse B">Warehouse B</option>
              <option value="Construction Site C">Construction Site C</option>
              <option value="Laboratory">Laboratory</option>
            </Select>

            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e?.target?.value)}
            >
              <option value="all">All Statuses</option>
              <option value="Under Investigation">Under Investigation</option>
              <option value="Awaiting Review">Awaiting Review</option>
              <option value="Recommendations Pending">Recommendations Pending</option>
              <option value="Completed">Completed</option>
            </Select>

            <Select
              value={dateRange}
              onChange={(e) => setDateRange(e?.target?.value)}
            >
              <option value="7days">Last 7 Days</option>
              <option value="30days">Last 30 Days</option>
              <option value="90days">Last 90 Days</option>
              <option value="year">This Year</option>
            </Select>
          </div>
        </div>

        {/* Incidents Table */}
        <IncidentTable incidents={filteredIncidents} />
      </div>
    </div>
  );
};

export default IncidentReportingDashboardContent;